import { useState } from 'react';
import { ArrowRight, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Mark from './Mark';

/**
 * The signed-out / unauthorized landing. The ONE place terracotta carries the
 * field — a first impression of the arena. Staff sign in with Google; invited
 * candidates ("Aspirants") without a Google account can request a passwordless
 * magic link (server-gated: a link is only emailed to an invited address, and the
 * response is uniform either way so it never reveals who is invited).
 */
export default function SignIn() {
  const { user, isAuthorized, authError, login, sendLink, submitEmailForLink, emailLinkState } =
    useAuth();
  const [busy, setBusy] = useState(false);

  const onGoogle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await login();
    } catch {
      /* surfaced via authError */
    } finally {
      setBusy(false);
    }
  };

  // Completing a magic-link sign-in (opened from the email on this device).
  if (emailLinkState === 'processing') {
    return (
      <Centered>
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-terracotta-500">
          <Mark className="h-7 w-7 animate-pulse text-terracotta-50" />
        </div>
        <p className="text-sm text-ink-600 dark:text-paper-200">Signing you in…</p>
      </Centered>
    );
  }

  // Cross-device: the link was opened on a different device than it was requested,
  // so we don't have the email locally — ask for it to complete sign-in.
  if (emailLinkState === 'needs_email') {
    return (
      <Centered>
        <Header />
        <p className="mt-4 text-[15px] text-ink-700 dark:text-paper-200">
          Confirm your email to finish signing in.
        </p>
        <EmailForm
          submitLabel="Confirm & sign in"
          onSubmit={(email) => submitEmailForLink(email)}
        />
      </Centered>
    );
  }

  // Signed in but the server said no (wrong domain / not invited / revoked).
  const notApproved = user && isAuthorized === false;

  return (
    <Centered>
      <Header />
      <p className="mt-4 text-[15px] leading-relaxed text-ink-700 dark:text-paper-200">
        Train together, rise together. Follow your kushtis, earn your Zor, and rise.
      </p>

      {notApproved ? (
        <div className="mt-7 rounded-xl border border-warn-500/40 bg-paper-0 p-4 text-sm text-ink-700 dark:bg-clay-raised dark:text-paper-200">
          <p className="font-semibold text-ink-900 dark:text-paper-50">Not in the akhara yet</p>
          <p className="mt-1 text-ink-500 dark:text-paper-300">
            You're signed in, but this account isn't in the akhara. Staff: use your{' '}
            <span className="font-medium">cloudcompliance.app</span> or{' '}
            <span className="font-medium">henoyo.ai</span> account. Invited candidates: use the exact
            email you were invited with.
          </p>
        </div>
      ) : (
        <>
          <button
            onClick={() => void onGoogle()}
            disabled={busy}
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-3 text-[15px] font-semibold text-white shadow-sm transition hover:bg-ink-700 disabled:opacity-60 dark:bg-paper-50 dark:text-ink-900 dark:hover:bg-paper-200"
          >
            {busy ? 'Opening…' : 'Continue with Google'}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>

          <MagicLinkRequest onSend={sendLink} />

          <p className="mt-4 text-xs text-ink-400">Your account is verified server-side.</p>
        </>
      )}

      {emailLinkState === 'error' && (
        <p className="mt-3 text-sm text-terracotta-700">
          That sign-in link didn't work — it may have expired. Request a new one below.
        </p>
      )}
      {authError && <p className="mt-3 text-sm text-terracotta-700">{authError}</p>}
    </Centered>
  );
}

function Header() {
  return (
    <>
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-terracotta-500 shadow-reward">
        <Mark className="h-9 w-9 text-terracotta-50" />
      </div>
      <h1 className="wordmark text-3xl text-ink-900 dark:text-paper-50">Akhara</h1>
      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-terracotta-600 dark:text-terracotta-300">
        by Henoyo
      </p>
    </>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-terracotta-50 to-paper-50 px-6 text-center dark:from-clay-raised dark:to-clay-floor">
      <div className="w-full max-w-sm animate-rise-in">{children}</div>
    </div>
  );
}

/** Invited-candidate magic-link request. Uniform confirmation (no enumeration). */
function MagicLinkRequest({ onSend }: { onSend: (email: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <div className="mt-4 rounded-xl border border-rule bg-paper-0 p-4 text-sm text-ink-600 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-200">
        If that email was invited, a sign-in link is on its way. Check your inbox.
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 hover:text-terracotta-600 dark:text-paper-300"
      >
        <Mail className="h-4 w-4" /> Invited without a Google account? Email me a link
      </button>
    );
  }

  return (
    <EmailForm
      submitLabel="Email me a sign-in link"
      onSubmit={async (email) => {
        await onSend(email);
        setSent(true);
      }}
    />
  );
}

/** A small email + submit form used by both the request and cross-device flows. */
function EmailForm({
  submitLabel,
  onSubmit,
}: {
  submitLabel: string;
  onSubmit: (email: string) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(email.trim());
    } catch {
      setErr('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-2">
      <input
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        required
        className="w-full rounded-xl border border-rule bg-paper-0 px-3.5 py-2.5 text-[15px] text-ink-900 outline-none ring-terracotta-200 placeholder:text-ink-300 focus:border-terracotta-300 focus:ring-2 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-50"
      />
      <button
        type="submit"
        disabled={busy}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-terracotta-500 px-5 py-3 text-[15px] font-semibold text-white shadow-sm transition hover:bg-terracotta-600 disabled:opacity-60"
      >
        <Mail className="h-4 w-4" />
        {busy ? 'Sending…' : submitLabel}
      </button>
      {err && <p className="text-sm text-terracotta-700">{err}</p>}
    </form>
  );
}
